/**
 * Cash Credit Bill — printable HTML invoice (A4).
 * Additive to Smart Bills: does not change create/payment/WhatsApp flows.
 * Layout modelled on standard GST cash-credit bill format.
 */

import axios from 'axios';
import { getApiBaseUrl } from '../services/httpClient';

const ACCENT = '#6B4C9A';
const HEADER_BG = '#C8BFE7';

const STATE_CODES = {
  'andaman and nicobar islands': 'AN',
  'andhra pradesh': 'AP',
  'arunachal pradesh': 'AR',
  assam: 'AS',
  bihar: 'BR',
  chandigarh: 'CH',
  chhattisgarh: 'CG',
  delhi: 'DL',
  goa: 'GA',
  gujarat: 'GJ',
  haryana: 'HR',
  'himachal pradesh': 'HP',
  'jammu and kashmir': 'JK',
  jharkhand: 'JH',
  karnataka: 'KA',
  kerala: 'KL',
  ladakh: 'LA',
  lakshadweep: 'LD',
  'madhya pradesh': 'MP',
  maharashtra: 'MH',
  manipur: 'MN',
  meghalaya: 'ML',
  mizoram: 'MZ',
  nagaland: 'NL',
  odisha: 'OD',
  puducherry: 'PY',
  punjab: 'PB',
  rajasthan: 'RJ',
  sikkim: 'SK',
  'tamil nadu': 'TN',
  telangana: 'TS',
  tripura: 'TR',
  'uttar pradesh': 'UP',
  uttarakhand: 'UK',
  'west bengal': 'WB',
};

const ONES = [
  '',
  'One',
  'Two',
  'Three',
  'Four',
  'Five',
  'Six',
  'Seven',
  'Eight',
  'Nine',
  'Ten',
  'Eleven',
  'Twelve',
  'Thirteen',
  'Fourteen',
  'Fifteen',
  'Sixteen',
  'Seventeen',
  'Eighteen',
  'Nineteen',
];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const formatInr = (value) => {
  const n = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
};

const twoDigitWords = (n) => {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10);
  const o = n % 10;
  return `${TENS[t]}${o ? ` ${ONES[o]}` : ''}`.trim();
};

const threeDigitWords = (n) => {
  if (n === 0) return '';
  if (n < 100) return twoDigitWords(n);
  const h = Math.floor(n / 100);
  const rest = n % 100;
  return `${ONES[h]} Hundred${rest ? ` ${twoDigitWords(rest)}` : ''}`.trim();
};

/** Indian numbering: Crore / Lakh / Thousand. */
export const amountInWordsInr = (amount) => {
  const num = Math.round((Number(amount) || 0) * 100) / 100;
  if (num === 0) return 'Zero Rupees Only';

  const whole = Math.floor(num);
  const paise = Math.round((num - whole) * 100);

  const crore = Math.floor(whole / 10000000);
  const lakh = Math.floor((whole % 10000000) / 100000);
  const thousand = Math.floor((whole % 100000) / 1000);
  const hundred = whole % 1000;

  const parts = [];
  if (crore) parts.push(`${threeDigitWords(crore)} Crore`);
  if (lakh) parts.push(`${threeDigitWords(lakh)} Lakh`);
  if (thousand) parts.push(`${threeDigitWords(thousand)} Thousand`);
  if (hundred) parts.push(threeDigitWords(hundred));

  let words = `${parts.join(' ').trim() || 'Zero'} Rupees`;
  if (paise > 0) words += ` and ${twoDigitWords(paise)} Paise`;
  return `${words} Only`;
};

export const resolveStateCode = (stateName) => {
  const key = String(stateName || '')
    .trim()
    .toLowerCase();
  if (!key) return '';
  if (key.length === 2) return key.toUpperCase();
  return STATE_CODES[key] || '';
};

const formatBillDate = (value) => {
  if (!value) return '—';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}-${mm}-${yyyy}`;
};

const buildShopAddress = (shop) => {
  const bits = [
    shop.address,
    shop.city,
    shop.state && shop.pincode ? `${shop.state}-${shop.pincode}` : shop.state || shop.pincode,
  ]
    .map((x) => String(x || '').trim())
    .filter(Boolean);
  return bits.join(', ');
};

const buildCustomerAddress = (bill) => {
  const bits = [
    bill.customerAddress || bill.customer_address,
    bill.customerCity || bill.customer_city,
    bill.customerPincode || bill.customer_pincode,
  ]
    .map((x) => String(x || '').trim())
    .filter(Boolean);
  return bits.join(', ') || '—';
};

const resolveBillTitle = (bill) => {
  const methods = bill.paymentMethods || bill.payment_methods || {};
  const credit = Number(methods.credit || 0);
  const due = Number(bill.dueAmount ?? bill.due_amount ?? 0);
  if (credit > 0 || due > 0) return 'CASH CREDIT BILL';
  const cash = Number(methods.cash || 0);
  const upi = Number(methods.upi || 0);
  const card = Number(methods.card || 0);
  if (cash > 0 && upi <= 0 && card <= 0) return 'CASH BILL';
  return 'TAX INVOICE';
};

/**
 * Normalize line items from View Bills / API shapes.
 */
export const normalizePrintLineItems = (bill) => {
  const services = bill.services || [];
  return services.map((service, index) => {
    const qty = Math.max(1, Number(service.quantity || 1));
    const serviceCharge = Number(
      service.serviceCharge ?? service.service_charge ?? 0,
    );
    const challana = Number(
      service.challanaAmount ?? service.challana_amount ?? 0,
    );
    const gst = Number(service.gstAmount ?? service.gst_amount ?? 0);
    const otherTax = Number(
      service.otherTaxAmount ?? service.other_tax_amount ?? 0,
    );
    const total = Number(service.total ?? 0);
    const taxable =
      serviceCharge > 0
        ? serviceCharge
        : Math.max(0, total - challana - gst - otherTax);
    const amount = total > 0 ? total : taxable + challana + gst + otherTax;
    const rate = qty > 0 ? taxable / qty : taxable;
    const name =
      service.name ||
      service.display_name ||
      service.service_name ||
      `Item ${index + 1}`;
    const descParts = [name];
    if (challana > 0) descParts.push(`(Challan ₹${formatInr(challana)})`);

    return {
      sno: index + 1,
      description: descParts.join(' '),
      hsn: service.hsnCode || service.hsn_code || '—',
      qty,
      rate,
      amount,
      gst,
      otherTax,
      challana,
      taxable,
    };
  });
};

export const buildShopProfileFromApi = (data = {}) => {
  const shopName =
    data.shop_name || data.business_name || data.full_name || 'Shop';
  const state = data.state || '';
  return {
    shopName: String(shopName).trim() || 'Shop',
    gstNumber: String(data.gst_number || data.gstin || '').trim(),
    address: buildShopAddress(data),
    email: String(data.email || '').trim(),
    phone: String(data.phone || '').trim(),
    state,
    stateCode: resolveStateCode(state),
  };
};

export const fetchShopProfileForPrint = async () => {
  try {
    const response = await axios.get(`${getApiBaseUrl()}/auth/business-profile`);
    const profile = response.data?.data || response.data || {};
    return buildShopProfileFromApi(profile);
  } catch {
    return buildShopProfileFromApi({});
  }
};

/**
 * Build printable HTML matching cash-credit bill sample.
 * @param {object} bill - mapped View Bills bill (or API bill)
 * @param {object} shop - { shopName, gstNumber, address, email, phone, state, stateCode }
 */
export const buildCashCreditBillHtml = (bill, shop = {}) => {
  const shopName = shop.shopName || 'Shop';
  const shopGst = shop.gstNumber || '—';
  const shopAddress = shop.address || '—';
  const shopEmail = shop.email || '';
  const shopPhone = shop.phone || '';
  const shopState = shop.state || '';
  const shopStateCode = shop.stateCode || resolveStateCode(shopState);

  const contactBits = [];
  if (shopEmail) contactBits.push(`e-mail : ${shopEmail}`);
  if (shopPhone) contactBits.push(`Ph. ${shopPhone}`);
  const contactLine = contactBits.join(', ') || '—';

  const clientName = bill.customerName || bill.customer_name || '—';
  const clientAddress = buildCustomerAddress(bill);
  const clientGstin =
    bill.customerGstin ||
    bill.customer_gstin ||
    bill.gstin ||
    '—';
  const clientState =
    bill.customerState || bill.customer_state || shopState || '';
  const clientStateCode =
    bill.customerStateCode ||
    bill.customer_state_code ||
    resolveStateCode(clientState);

  const invoiceNo = bill.billNo || bill.bill_number || bill.id || '—';
  const billDate = formatBillDate(bill.billDate || bill.bill_date);
  const title = resolveBillTitle(bill);

  const lines = normalizePrintLineItems(bill);
  const taxableTotal = lines.reduce((s, l) => s + (Number(l.taxable) || 0), 0);
  const breakdown = bill.tax_breakdown || bill.taxBreakdown || {};
  const gstTotal =
    Number(breakdown.gst_total ?? breakdown.gstTotal) ||
    lines.reduce((s, l) => s + (Number(l.gst) || 0) * (Number(l.qty) || 1), 0);
  const otherTaxLines = Array.isArray(breakdown.other_taxes)
    ? breakdown.other_taxes
    : Array.isArray(breakdown.otherTaxes)
      ? breakdown.otherTaxes
      : [];
  const lineTotal = lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const grandTotal = Number(
    bill.totalAmount ?? bill.total_amount ?? lineTotal ?? 0,
  );
  const discount =
    Number(bill.discountAmount ?? bill.discount_amount ?? 0) +
    Number(bill.couponDiscount ?? bill.coupon_discount ?? 0);

  // Intra-state style: split GST equally into CGST / SGST when gst present.
  let cgst = gstTotal / 2;
  let sgst = gstTotal / 2;
  if (gstTotal <= 0 && grandTotal > taxableTotal && taxableTotal > 0) {
    const implied = grandTotal - taxableTotal - discount;
    if (implied > 0) {
      cgst = implied / 2;
      sgst = implied / 2;
    }
  }

  const cgstRate =
    taxableTotal > 0 && cgst > 0
      ? ((cgst / taxableTotal) * 100).toFixed(0)
      : '';
  const sgstRate =
    taxableTotal > 0 && sgst > 0
      ? ((sgst / taxableTotal) * 100).toFixed(0)
      : '';

  const totalValue = taxableTotal > 0 ? taxableTotal : lineTotal;
  const minRows = Math.max(8, lines.length);
  const emptyRows = Math.max(0, minRows - lines.length);

  const lineRowsHtml = lines
    .map(
      (line) => `
      <tr>
        <td class="c-sno">${line.sno}</td>
        <td class="c-desc">${escapeHtml(line.description)}</td>
        <td class="c-hsn">${escapeHtml(line.hsn)}</td>
        <td class="c-qty">${line.qty}</td>
        <td class="c-rate">${formatInr(line.rate)}</td>
        <td class="c-amt">${formatInr(line.amount)}</td>
      </tr>`,
    )
    .join('');

  const emptyRowsHtml = Array.from({ length: emptyRows })
    .map(
      () => `
      <tr class="empty-row">
        <td class="c-sno">&nbsp;</td>
        <td class="c-desc">&nbsp;</td>
        <td class="c-hsn">&nbsp;</td>
        <td class="c-qty">&nbsp;</td>
        <td class="c-rate">&nbsp;</td>
        <td class="c-amt">&nbsp;</td>
      </tr>`,
    )
    .join('');

  const paid = Number(bill.paidAmount ?? bill.paid_amount ?? 0);
  const due = Number(bill.dueAmount ?? bill.due_amount ?? 0);
  const paymentNote =
    due > 0
      ? `Paid: ₹${formatInr(paid)} · Due: ₹${formatInr(due)}`
      : paid > 0
        ? `Paid: ₹${formatInr(paid)}`
        : '';

  const words = amountInWordsInr(grandTotal);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(title)} — ${escapeHtml(invoiceNo)}</title>
  <style>
    @page { size: A4; margin: 12mm; }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      padding: 16px;
      font-family: Arial, Helvetica, sans-serif;
      color: #111;
      background: #fff;
      font-size: 12px;
    }
    .sheet {
      max-width: 210mm;
      margin: 0 auto;
      border: 1.5px solid #111;
      padding: 14px 16px 18px;
    }
    .shop-name {
      font-size: 22px;
      font-weight: 700;
      color: ${ACCENT};
      letter-spacing: 0.3px;
      margin: 0 0 4px;
      text-transform: uppercase;
    }
    .shop-meta { margin: 2px 0; line-height: 1.45; }
    .shop-meta strong { font-weight: 700; }
    .rule {
      border: none;
      border-top: 1.5px solid #111;
      margin: 10px 0 8px;
    }
    .bill-title {
      text-align: center;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: 1px;
      margin: 4px 0 10px;
      text-transform: uppercase;
    }
    .party-box {
      border: 1.5px solid #111;
      margin-bottom: 10px;
    }
    .party-top {
      display: grid;
      grid-template-columns: 1.4fr 0.8fr;
      background: ${HEADER_BG};
      border-bottom: 1px solid #111;
    }
    .party-top > div { padding: 8px 10px; }
    .party-top .right { border-left: 1px solid #111; }
    .party-bottom {
      display: grid;
      grid-template-columns: 1.4fr 0.8fr;
      padding: 8px 10px;
    }
    .party-bottom .right { border-left: 1px solid #111; padding-left: 10px; }
    .party-label { font-weight: 700; }
    .delivery-head {
      font-weight: 700;
      text-decoration: underline;
      margin-bottom: 4px;
    }
    table.items {
      width: 100%;
      border-collapse: collapse;
      table-layout: fixed;
    }
    table.items th, table.items td {
      border: 1px solid #111;
      padding: 5px 6px;
      vertical-align: top;
    }
    table.items th {
      background: ${HEADER_BG};
      font-weight: 700;
      text-align: center;
    }
    .c-sno { width: 7%; text-align: center; }
    .c-desc { width: 42%; text-align: left; }
    .c-hsn { width: 14%; text-align: center; }
    .c-qty { width: 9%; text-align: center; }
    .c-rate { width: 14%; text-align: right; }
    .c-amt { width: 14%; text-align: right; }
    tr.empty-row td { height: 18px; }
    .totals-wrap { margin-top: -1px; }
    .totals-table {
      width: 100%;
      border-collapse: collapse;
    }
    .totals-table td {
      border: 1px solid #111;
      padding: 6px 8px;
    }
    .totals-table .label { text-align: right; font-weight: 600; width: 72%; }
    .totals-table .value { text-align: right; width: 28%; }
    .totals-table .hl { background: ${HEADER_BG}; font-weight: 700; }
    .footer {
      display: grid;
      grid-template-columns: 1.2fr 0.8fr;
      gap: 12px;
      margin-top: 14px;
      min-height: 90px;
    }
    .words { line-height: 1.5; }
    .sign {
      text-align: right;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      min-height: 90px;
    }
    .sign-for { font-weight: 700; }
    .sign-auth { margin-top: 48px; }
    .pay-note {
      margin-top: 8px;
      font-size: 11px;
      color: #333;
    }
    .no-print {
      max-width: 210mm;
      margin: 0 auto 12px;
      display: flex;
      gap: 8px;
      justify-content: flex-end;
    }
    .no-print button {
      border: none;
      border-radius: 8px;
      padding: 10px 16px;
      font-weight: 600;
      cursor: pointer;
      font-size: 14px;
    }
    .btn-print { background: #e0e7ff; color: #312e81; }
    .btn-close { background: #f1f5f9; color: #475569; }
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
      .sheet { border: 1.5px solid #111; max-width: none; }
    }
  </style>
</head>
<body>
  <div class="no-print">
    <button class="btn-print" onclick="window.print()">🖨️ Print Bill</button>
    <button class="btn-close" onclick="window.close()">Close</button>
  </div>
  <div class="sheet">
    <h1 class="shop-name">${escapeHtml(shopName)}</h1>
    <div class="shop-meta"><strong>GST No.</strong> ${escapeHtml(shopGst)}</div>
    <div class="shop-meta">${escapeHtml(shopAddress)}</div>
    <div class="shop-meta">${escapeHtml(contactLine)}</div>
    <div class="shop-meta">
      State Name : ${escapeHtml(shopState || '—')},
      State Code : ${escapeHtml(shopStateCode || '—')}
    </div>
    <hr class="rule" />
    <div class="bill-title">${escapeHtml(title)}</div>

    <div class="party-box">
      <div class="party-top">
        <div>
          <div><span class="party-label">Client Name :</span> ${escapeHtml(clientName)}</div>
          <div><span class="party-label">Address :</span> ${escapeHtml(clientAddress)}</div>
          <div><span class="party-label">GSTIN :</span> ${escapeHtml(clientGstin)}</div>
        </div>
        <div class="right">
          <div><span class="party-label">Date :</span> ${escapeHtml(billDate)}</div>
          <div><span class="party-label">Invoice No :</span> ${escapeHtml(invoiceNo)}</div>
        </div>
      </div>
      <div class="party-bottom">
        <div>
          <div class="delivery-head">Delivery Address:</div>
          <div><span class="party-label">Client Name :</span> ${escapeHtml(clientName)}</div>
          <div><span class="party-label">Address :</span> ${escapeHtml(clientAddress)}</div>
          <div><span class="party-label">GSTIN :</span> ${escapeHtml(clientGstin)}</div>
        </div>
        <div class="right">
          <div><span class="party-label">State Name:</span> ${escapeHtml(clientState || '—')}</div>
          <div><span class="party-label">State Code:</span> ${escapeHtml(clientStateCode || '—')}</div>
        </div>
      </div>
    </div>

    <table class="items">
      <thead>
        <tr>
          <th class="c-sno">S.No</th>
          <th class="c-desc">Description</th>
          <th class="c-hsn">HSN Code</th>
          <th class="c-qty">Qty</th>
          <th class="c-rate">Rate</th>
          <th class="c-amt">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${lineRowsHtml}
        ${emptyRowsHtml}
      </tbody>
    </table>

    <div class="totals-wrap">
      <table class="totals-table">
        <tr class="hl">
          <td class="label">Total Value</td>
          <td class="value">${formatInr(totalValue)}</td>
        </tr>
        ${
          discount > 0
            ? `<tr>
          <td class="label">Less : Discount</td>
          <td class="value">${formatInr(discount)}</td>
        </tr>`
            : ''
        }
        <tr>
          <td class="label">Add : CGST${cgstRate ? ` ${cgstRate}%` : ''}</td>
          <td class="value">${formatInr(cgst)}</td>
        </tr>
        <tr>
          <td class="label">Add : SGST${sgstRate ? ` ${sgstRate}%` : ''}</td>
          <td class="value">${formatInr(sgst)}</td>
        </tr>
        ${otherTaxLines
          .filter((tax) => Number(tax.amount) > 0)
          .map(
            (tax) => `<tr>
          <td class="label">Add : ${escapeHtml(tax.label || 'Other tax')}${tax.percent ? ` ${tax.percent}%` : ''}</td>
          <td class="value">${formatInr(tax.amount)}</td>
        </tr>`,
          )
          .join('')}
        <tr class="hl">
          <td class="label">Grand Total</td>
          <td class="value">${formatInr(grandTotal)}</td>
        </tr>
      </table>
    </div>

    ${paymentNote ? `<div class="pay-note">${escapeHtml(paymentNote)}</div>` : ''}

    <div class="footer">
      <div class="words">
        <strong>Amount in Words:</strong> ${escapeHtml(words)}
      </div>
      <div class="sign">
        <div class="sign-for">For ${escapeHtml(shopName)}</div>
        <div class="sign-auth">Authorised Signature</div>
      </div>
    </div>
  </div>
  <script>
    window.addEventListener('load', function () {
      setTimeout(function () { window.focus(); }, 100);
    });
  </script>
</body>
</html>`;
};

/**
 * Open printable cash-credit bill in a new window.
 */
export const openCashCreditBillPrint = async (bill, shopOverride = null) => {
  const shop = shopOverride || (await fetchShopProfileForPrint());
  const html = buildCashCreditBillHtml(bill, shop);
  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1100');
  if (!printWindow) {
    throw new Error('Pop-up blocked. Allow pop-ups to print the bill.');
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  return { printWindow, html, shop };
};

/** Download HTML invoice file (for WhatsApp attach / email). */
export const downloadCashCreditBillHtml = (bill, html, shop = {}) => {
  const invoiceNo = String(bill.billNo || bill.bill_number || bill.id || 'bill')
    .replace(/[^\w.-]+/g, '_');
  const shopSlug = String(shop.shopName || 'shop')
    .replace(/[^\w.-]+/g, '_')
    .slice(0, 40);
  const filename = `${shopSlug}_bill_${invoiceNo}.html`;
  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
  return filename;
};

/** WhatsApp text for printable HTML invoice (customer number). */
export const buildPrintBillWhatsAppMessage = (bill, shop = {}, filename = '') => {
  const name = bill.customerName || bill.customer_name || 'Customer';
  const invoiceNo = bill.billNo || bill.bill_number || bill.id || '—';
  const total = formatInr(bill.totalAmount ?? bill.total_amount ?? 0);
  const paid = formatInr(bill.paidAmount ?? bill.paid_amount ?? 0);
  const due = Number(bill.dueAmount ?? bill.due_amount ?? 0);
  const shopName = shop.shopName || 'Shop';
  const lines = normalizePrintLineItems(bill)
    .map((l) => `${l.sno}. ${l.description} × ${l.qty} = ₹${formatInr(l.amount)}`)
    .join('\n');

  let msg = `📄 *Tax / Cash Credit Bill*\n`;
  msg += `━━━━━━━━━━━━━━━━━━━━\n`;
  msg += `🏪 *${shopName}*\n`;
  if (shop.gstNumber) msg += `GST: ${shop.gstNumber}\n`;
  if (shop.address) msg += `📍 ${shop.address}\n`;
  msg += `\n👤 ${name}\n`;
  msg += `🆔 Invoice: ${invoiceNo}\n`;
  msg += `📅 ${formatBillDate(bill.billDate || bill.bill_date)}\n\n`;
  msg += `📦 *Items / Services:*\n${lines || '—'}\n\n`;
  msg += `💰 Grand Total: ₹${total}\n`;
  msg += `✅ Paid: ₹${paid}\n`;
  if (due > 0) msg += `⏳ Due: ₹${formatInr(due)}\n`;
  msg += `\n━━━━━━━━━━━━━━━━━━━━\n`;
  if (filename) {
    msg += `📎 HTML invoice file: *${filename}*\n`;
    msg += `Please attach / open this HTML bill (Print → Save as PDF if needed).\n\n`;
  } else {
    msg += `📎 Printable HTML invoice opened at the shop counter.\n\n`;
  }
  msg += `🙏 Thank you for your business!\n`;
  msg += `For ${shopName}`;
  return msg;
};

/**
 * Open printable HTML + download .html + WhatsApp to customer number.
 * Additive — does not change bill create/payment flows.
 */
export const openCashCreditBillPrintAndSend = async (bill, shopOverride = null) => {
  const phoneRaw = String(bill.customerPhone || bill.customer_phone || '').replace(/\D/g, '');
  const phone = phoneRaw.length >= 10 ? phoneRaw.slice(-10) : phoneRaw;
  if (phone.length !== 10) {
    throw new Error('Customer phone number is required to send the HTML bill');
  }

  const shop = shopOverride || (await fetchShopProfileForPrint());
  const html = buildCashCreditBillHtml(bill, shop);

  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=900,height=1100');
  if (printWindow) {
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  }

  const filename = downloadCashCreditBillHtml(bill, html, shop);
  const message = buildPrintBillWhatsAppMessage(bill, shop, filename);
  const whatsappUrl = `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
  window.open(whatsappUrl, '_blank', 'noopener,noreferrer');

  return { filename, phone, htmlOpened: Boolean(printWindow) };
};
